import React, { useState } from 'react';
import type { MultiFactorResolver, UserCredential } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { mfaError, resolveMfaChallenge, sendMfaCode } from '../common/mfa';
import { mfaStyles as styles } from './mfaStyles';
import { useMfaDialog } from './useMfaDialog';
import { useMfaCaptcha } from './useMfaCaptcha';

interface Props {
    resolver: MultiFactorResolver;
    onComplete: (credential: UserCredential) => void;
    onCancel: () => void;
}

export default function MfaChallenge({ resolver, onComplete, onCancel }: Props) {
    const supported = resolver.hints.filter(hint => hint.factorId === 'phone' || hint.factorId === 'totp');
    const [factorUid, setFactorUid] = useState(() => supported.find(hint => hint.factorId === 'phone')?.uid || supported[0]?.uid || '');
    const [code, setCode] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [busy, setBusy] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [resendAt, setResendAt] = useState(0);
    const panel = useMfaDialog(onCancel, busy && !sending);
    const selected = supported.find(hint => hint.uid === factorUid);
    const captcha = useMfaCaptcha(selected?.factorId === 'phone', factorUid);

    const send = async () => {
        setSending(true);
        setBusy(true);
        setError('');
        setCode('');
        setVerificationId('');
        try {
            setVerificationId(await captcha.verify(verifier => sendMfaCode(auth, verifier, resolver, factorUid)));
            setResendAt(Date.now() + 60_000);
        } catch (reason) {
            setError(mfaError(reason));
        } finally {
            captcha.reset();
            setSending(false);
            setBusy(false);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            onComplete(await resolveMfaChallenge(resolver, factorUid, code, verificationId));
        } catch (reason) {
            setError(mfaError(reason));
            setBusy(false);
        }
    };

    return <div className={styles.overlay}>
        <section ref={panel} className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="mfa-challenge-title">
            <h1 id="mfa-challenge-title" className="text-2xl font-bold">Verify your sign-in</h1>
            <p className={styles.text}>Enter a code from one of your verified security methods.</p>
            {error && !captcha.error && <p role="alert" className={styles.error}>{error}</p>}
            <form onSubmit={submit} className="space-y-4">
                <label className="block text-sm font-semibold">Verification method
                    <select className={styles.input} value={factorUid} disabled={busy} onChange={event => {
                        setFactorUid(event.target.value);
                        setVerificationId('');
                        setCode('');
                        setError('');
                        setResendAt(0);
                    }}>
                        {supported.map(hint => <option key={hint.uid} value={hint.uid}>{hint.factorId === 'phone'
                            ? `Text message: ${'phoneNumber' in hint ? hint.phoneNumber : hint.displayName || 'phone'}`
                            : `Authenticator app: ${hint.displayName || 'app'}`}</option>)}
                    </select>
                </label>
                <div id={captcha.id} hidden={selected?.factorId !== 'phone'} />
                {captcha.error && <p role="alert" className={styles.error}>{captcha.error}<button type="button" className="ml-2 underline" disabled={busy} onClick={() => { setError(''); captcha.reset(); }}>Retry security check</button></p>}
                {selected?.factorId === 'phone' && <button type="button" className={styles.secondary} disabled={busy || !captcha.ready} onClick={() => {
                    if (Date.now() < resendAt)
                        setError('Wait one minute before requesting another text.');
                    else
                        void send();
                }}>{verificationId ? 'Send another code' : 'Send text message'}</button>}
                {verificationId && <p role="status" className={styles.notice}>A code was sent to your selected phone number.</p>}
                <label className="block text-sm font-semibold">Six-digit code
                    <input autoFocus className={styles.input} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                        value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} required disabled={busy} />
                </label>
                <div className="flex flex-wrap gap-3"><button className={styles.button} disabled={busy || !supported.length || selected?.factorId === 'phone' && !verificationId}>{busy ? 'Verifying…' : 'Verify and continue'}</button>
                    <button type="button" className={styles.secondary} disabled={busy && !sending} onClick={onCancel}>Cancel sign-in</button></div>
            </form>
            <p className={styles.text}>Lost access? Try another enrolled method, or <a className="font-semibold text-blue-600 underline dark:text-blue-400" href="mailto:support@eprims.com?subject=MFA%20account%20recovery">contact support for identity verification</a>. A password reset does not remove MFA.</p>
        </section>
    </div>;
}
